import {MessageSquare, Package, Receipt, Truck, User} from "lucide-react";
import type {AdminOrderDetail} from "@corporatesclub/shared";
import {OrderDivider, OrderField, OrderFieldGrid, OrderSection} from "./OrderSection";

type Props = {detail: AdminOrderDetail};

export function OrderSummaryTab({detail}: Props) {
  const shipping = detail.delivery.shippingCost ?? 0;
  const linesSubtotal = detail.linesSubtotal ?? detail.lines.reduce((s, l) => s + l.lineTotal, 0);
  const discountTotal = detail.discountTotal ?? 0;

  return (
    <div className="space-y-4">
      <OrderSection title="Client" icon={<User />}>
        <OrderFieldGrid>
          <OrderField label="Name">{detail.client.nume}</OrderField>
          <OrderField label="Email">{detail.client.email || "—"}</OrderField>
          <OrderField label="Phone">{detail.client.telefon || "—"}</OrderField>
          <OrderField label="Payment">{detail.payment}</OrderField>
        </OrderFieldGrid>
      </OrderSection>

      <OrderSection title="Products" icon={<Package />} description={`${detail.lines.length} line${detail.lines.length === 1 ? "" : "s"}`} dense>
        <ul className="divide-y divide-border/60">
          {detail.lines.map((l) => (
            <li key={l.productId} className="flex items-start justify-between gap-3 py-3 first:pt-0 last:pb-0">
              <div className="min-w-0">
                {l.brandName ? (
                  <p className="text-sm font-bold text-foreground">{l.brandName}</p>
                ) : null}
                <p className="truncate text-sm font-medium">{l.name}</p>
                <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">
                  {l.quantity} × {l.unitPrice.toFixed(2)} lei
                  {l.stock != null ? ` · Stock: ${l.stock}` : ""}
                </p>
              </div>
              <span className="shrink-0 text-sm font-semibold tabular-nums">{l.lineTotal.toFixed(2)} lei</span>
            </li>
          ))}
        </ul>
      </OrderSection>

      <OrderSection title="Delivery & totals" icon={<Truck />}>
        <OrderFieldGrid>
          <OrderField label="Courier">{detail.delivery.curierLabel || "—"}</OrderField>
          <OrderField label="AWB">{detail.delivery.awb || "—"}</OrderField>
          <OrderField label="Address" span={2}>
            {[detail.delivery.adresa, detail.delivery.localitate, detail.delivery.judetNume, detail.delivery.codPostal]
              .filter(Boolean)
              .join(", ") || "—"}
          </OrderField>
        </OrderFieldGrid>
        <OrderDivider />
        <div className="space-y-2 text-sm">
          <div className="flex justify-between text-muted-foreground">
            <span>Subtotal</span>
            <span className="tabular-nums text-foreground">{linesSubtotal.toFixed(2)} lei</span>
          </div>
          <div className="flex justify-between text-muted-foreground">
            <span>Shipping</span>
            <span className="tabular-nums text-foreground">{shipping.toFixed(2)} lei</span>
          </div>
          {discountTotal > 0 ? (
            <div className="flex justify-between text-muted-foreground">
              <span>Discounts</span>
              <span className="tabular-nums text-foreground">−{discountTotal.toFixed(2)} lei</span>
            </div>
          ) : null}
          <div className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2.5">
            <span className="flex items-center gap-1.5 text-sm font-semibold">
              <Receipt className="h-3.5 w-3.5 text-muted-foreground" />
              Total
            </span>
            <span className="text-base font-semibold tabular-nums">{detail.total.toFixed(2)} lei</span>
          </div>
        </div>
      </OrderSection>

      {detail.mesaj ? (
        <OrderSection title="Customer message" icon={<MessageSquare />}>
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">{detail.mesaj}</p>
        </OrderSection>
      ) : null}
    </div>
  );
}
